import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enGB } from "date-fns/locale";
import { ACCOUNTS } from "../../constants";
import { formatLocalDateOnly } from "../shared utils/dateFormatters";

const DISCOUNT_PREFIX = "DISCOUNT:";

function parseDiscountReason(paymentAccount) {
  const s = (paymentAccount || "").trim();
  if (!s.toUpperCase().startsWith(DISCOUNT_PREFIX)) return "";
  return s.slice(DISCOUNT_PREFIX.length).trim();
}

function buildDiscountAccount(reason) {
  const r = (reason || "").trim();
  return r ? `${DISCOUNT_PREFIX} ${r}` : `${DISCOUNT_PREFIX} (no reason)`;
}

function toInitialDate(value) {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? new Date() : parsed;
}

export default function GroupPaymentDialog({
  open,
  onClose,
  onSave,
  initialData = null,
  saving = false,
}) {
  const editing = !!initialData?.id;

  const initialIsDiscount = useMemo(() => {
    const acc = initialData?.paymentAccount || "";
    return acc.toUpperCase().startsWith(DISCOUNT_PREFIX);
  }, [initialData]);

  const [isDiscount, setIsDiscount] = useState(initialIsDiscount);
  const [discountReason, setDiscountReason] = useState(
    parseDiscountReason(initialData?.paymentAccount)
  );
  const [paymentDate, setPaymentDate] = useState(
    toInitialDate(initialData?.paymentDate)
  );
  const [amount, setAmount] = useState(
    initialData?.paymentAmount != null ? String(initialData.paymentAmount) : ""
  );
  const [account, setAccount] = useState(
    initialIsDiscount ? "" : initialData?.paymentAccount || ""
  );
  const [notes, setNotes] = useState(initialData?.notes || "");

  useEffect(() => {
    if (!open) return;
    const acc = initialData?.paymentAccount || "";
    const disc = acc.toUpperCase().startsWith(DISCOUNT_PREFIX);
    setIsDiscount(disc);
    setDiscountReason(parseDiscountReason(acc));
    setPaymentDate(toInitialDate(initialData?.paymentDate));
    setAmount(
      initialData?.paymentAmount != null
        ? String(initialData.paymentAmount)
        : ""
    );
    setAccount(disc ? "" : acc);
    setNotes(initialData?.notes || "");
  }, [open, initialData]);

  const parsedAmount = Number(amount);
  const canSave =
    !saving &&
    Number.isFinite(parsedAmount) &&
    parsedAmount > 0 &&
    (isDiscount || !!account);

  const handleSave = () => {
    const payload = {
      paymentDate: formatLocalDateOnly(paymentDate),
      paymentAmount: Math.abs(parsedAmount),
      paymentAccount: isDiscount ? buildDiscountAccount(discountReason) : account,
      notes: notes.trim(),
    };
    onSave(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editing ? "Edit group payment" : "Record group payment"}
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDiscount}
                  onChange={(e) => setIsDiscount(e.target.checked)}
                />
              }
              label="This is a discount / adjustment"
            />

            <DatePicker
              label="Payment date"
              value={paymentDate}
              onChange={(value) => setPaymentDate(value || new Date())}
              slotProps={{ textField: { fullWidth: true, size: "small" } }}
            />

            <TextField
              label="Amount (£)"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
              size="small"
              required
            />

            {isDiscount ? (
              <TextField
                label="Discount reason"
                value={discountReason}
                onChange={(e) => setDiscountReason(e.target.value)}
                placeholder="e.g. Early bird, Sibling, Agent goodwill"
                fullWidth
                size="small"
              />
            ) : (
              <FormControl fullWidth size="small" required>
                <InputLabel id="gp-account-label">Account</InputLabel>
                <Select
                  labelId="gp-account-label"
                  value={account}
                  label="Account"
                  onChange={(e) => setAccount(e.target.value)}
                >
                  {ACCOUNTS.map((a) => (
                    <MenuItem key={a.value} value={a.value}>
                      {a.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={2}
              fullWidth
              size="small"
            />
          </Stack>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
        >
          {saving ? "Saving…" : editing ? "Save" : "Record payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
